import { Eye, Trash2, Copy, Share2, MoreVertical, Palette } from "lucide-react";
import type { MapStyleRowActionsProps } from "../../contracts/map-styles";
import { cn } from "../../lib/utils";

export function MapStyleRowActions({
  actions,
  onAction,
  className,
}: MapStyleRowActionsProps) {
  if (!actions.length) return null;

  // Prioritize "workbench" and "inspect"
  const workbenchAction = actions.find((a) => a.id === "workbench");
  const inspectAction = actions.find((a) => a.id === "inspect");
  const duplicateAction = actions.find((a) => a.id === "duplicate");
  const shareAction = actions.find((a) => a.id === "share");
  const deleteAction = actions.find((a) => a.id === "delete");

  const otherActions = actions.filter(
    (a) => !["workbench", "inspect", "duplicate", "share", "delete"].includes(a.id)
  );

  return (
    <div className={cn("flex w-full flex-wrap items-center gap-2 md:w-auto md:flex-nowrap", className)}>
      {workbenchAction && (
        <button
          onClick={() => onAction(workbenchAction.id)}
          disabled={workbenchAction.disabled}
          title={workbenchAction.description || workbenchAction.label}
          className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-1 text-[11px] font-bold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:opacity-50 sm:flex-none"
        >
          <Palette className="h-3.5 w-3.5" />
          {workbenchAction.label}
        </button>
      )}

      <div className={cn("flex items-center gap-1", workbenchAction ? "ml-auto" : "")}>
        {inspectAction && (
          <button
            onClick={() => onAction(inspectAction.id)}
            disabled={inspectAction.disabled}
            title={inspectAction.description || inspectAction.label}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <Eye className="h-3.5 w-3.5" />
            <span className="sr-only">{inspectAction.label}</span>
          </button>
        )}

        {duplicateAction && (
          <button
            onClick={() => onAction(duplicateAction.id)}
            disabled={duplicateAction.disabled}
            title={duplicateAction.description || duplicateAction.label}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <Copy className="h-3.5 w-3.5" />
            <span className="sr-only">{duplicateAction.label}</span>
          </button>
        )}

        {shareAction && (
          <button
            onClick={() => onAction(shareAction.id)}
            disabled={shareAction.disabled}
            title={shareAction.description || shareAction.label}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <Share2 className="h-3.5 w-3.5" />
            <span className="sr-only">{shareAction.label}</span>
          </button>
        )}

        {otherActions.map((action) => (
          <button
            key={action.id}
            onClick={() => onAction(action.id)}
            disabled={action.disabled}
            title={action.description || action.label}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <MoreVertical className="h-3.5 w-3.5" />
            <span className="sr-only">{action.label}</span>
          </button>
        ))}

        {deleteAction && (
          <button
            onClick={() => onAction(deleteAction.id)}
            disabled={deleteAction.disabled}
            title={deleteAction.description || deleteAction.label}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
            <span className="sr-only">{deleteAction.label}</span>
          </button>
        )}
      </div>
    </div>
  );
}
